/**
 * Mothers waiting on her answer.
 *
 * This is the one screen on the doula surface where somebody else is waiting.
 * Everything else — her caseload, her payments, her profile — is retrospective;
 * this is a decision with a person on the other end of it, which is why it is
 * its own page rather than a section of the caseload.
 *
 * WHAT SHE IS SHOWN, AND WHAT SHE IS NOT
 * Shortlist-level facts only: a first name, the languages the mother asked for,
 * what matters most to her, roughly where she is, when she is due. No surname,
 * no address, no clinical history. §3.14 is explicit that a doula decides
 * "without gaining her full clinical history before accepting", and the limit
 * is enforced in the endpoint's SELECT list — this page could not show more
 * even if it tried.
 *
 * DECLINING ASKS FOR A REASON AND DOES NOT REQUIRE ONE. A reason helps the next
 * match; demanding one turns a decline into a negotiation, and a doula who
 * cannot take a client this month should be able to say so in one tap.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, CalendarClock, Check, Inbox, MapPin, X } from 'lucide-react';

interface Offer {
  request_id: number;
  offered_at: string;
  rank: number;
  member_first_name: string;
  preferred_languages: string[];
  community_preference: string | null;
  requested_specialisms: string[];
  due_date: string | null;
  preferred_area: string | null;
  what_matters_most: string | null;
  respond_by_token: string | null;
}

function when(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function DoulaOffersPage() {
  const { t } = useTranslation();
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [decliningId, setDecliningId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOffers(await authFetchJson<Offer[]>('/api/doula/v1/offers'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.offersFailed', 'We could not load your offers.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const respond = async (requestId: number, accept: boolean) => {
    setBusy(requestId);
    setError(null);
    try {
      await authFetchJson(`/api/doula/v1/offers/${requestId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ accept, reason: reason.trim() || null }),
      });
      setDone(
        accept
          ? t('doula.offerAccepted', 'Accepted. She is on your client list now.')
          : t('doula.offerDeclined', 'Declined. She will be offered to someone else.'),
      );
      setDecliningId(null);
      setReason('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.offerFailed', 'We could not record your answer.'));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <PageState variant="loading" />;

  const list = offers ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t('doula.offers', 'Mothers waiting on you')}
        description={t(
          'doula.offersDescription',
          'Each of these chose you. They are waiting for your answer before anyone else is asked.',
        )}
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {done && (
        <div className="mb-4 flex items-start gap-2 rounded-md border p-4 text-sm">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{done}</span>
        </div>
      )}

      {list.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('doula.noOffers', 'Nobody is waiting on you right now.')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('doula.noOffersHint', 'When a mother chooses you, she appears here.')}
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {list.map((o) => (
            <li key={o.request_id} className="rounded-md border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-base font-medium">{o.member_first_name}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('doula.offeredOn', 'asked you on {{date}}', { date: when(o.offered_at) })}
                  </p>
                </div>
                {o.due_date && (
                  <p className="flex items-center gap-1 text-sm font-medium">
                    <CalendarClock className="h-4 w-4" />
                    {t('doula.dueOn', 'due {{date}}', { date: when(o.due_date) })}
                  </p>
                )}
              </div>

              {/* What she asked for, which is the whole basis of the decision. */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {o.preferred_languages.map((l) => (
                  <Badge key={l} variant="secondary">
                    {l}
                  </Badge>
                ))}
                {o.community_preference && <Badge variant="outline">{o.community_preference}</Badge>}
                {o.requested_specialisms.map((s) => (
                  <Badge key={s} variant="outline">
                    {s}
                  </Badge>
                ))}
              </div>

              {o.preferred_area && (
                <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  {o.preferred_area}
                </p>
              )}

              {/* In her own words, and given room. This is the part a doula
                  actually reads before deciding. */}
              {o.what_matters_most && (
                <blockquote className="mt-3 border-l-2 pl-3 text-sm italic">
                  {o.what_matters_most}
                </blockquote>
              )}

              {decliningId === o.request_id ? (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder={t(
                      'doula.declineReasonPlaceholder',
                      'Anything you want us to know — optional.',
                    )}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy === o.request_id}
                      onClick={() => void respond(o.request_id, false)}
                    >
                      {t('doula.confirmDecline', 'Confirm decline')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDecliningId(null);
                        setReason('');
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
                  <Button
                    size="sm"
                    disabled={busy === o.request_id}
                    onClick={() => void respond(o.request_id, true)}
                    className="flex items-center gap-1.5"
                  >
                    <Check className="h-4 w-4" />
                    {t('doula.accept', 'Accept')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === o.request_id}
                    onClick={() => setDecliningId(o.request_id)}
                    className="flex items-center gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    {t('doula.decline', 'Decline')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageContainer>
  );
}
